import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { Button, Card, Select, FormControl } from '@mui/material';
import { colors, shapes } from './theme';

/**
 * Styled Table Cell component with theme colors
 * Use this instead of creating StyledTableCell in each component
 */
export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.action.hover : 'transparent',
    color: theme.palette.text.primary,
    fontWeight: 600,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

/**
 * Styled Table Row component with alternating row colors
 * Use this instead of creating StyledTableRow in each component
 */
export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: colors.table.rowOdd,
  },
  '&:hover': {
    backgroundColor: colors.table.rowHover,
  },
  // Hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

/**
 * Styled Button with gradient background (for primary action buttons)
 * Example: <GradientButton variant="contained" size="large">Start Test</GradientButton>
 */
export const GradientButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(90deg, ${colors.primary.main} 0%, ${colors.primary.light} 100%)`,
  borderRadius: shapes.button.large,
  padding: '12px 32px',
  fontWeight: 'bold',
  fontSize: '1rem',
  '&:hover': {
    background: `linear-gradient(90deg, ${colors.primary.dark} 0%, ${colors.primary.main} 100%)`,
  },
}));

/**
 * Styled Card with consistent styling
 * Example: <StyledCard><CardContent>...</CardContent></StyledCard>
 */
export const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: shapes.card.default,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
}));

/**
 * Styled Card with large border radius
 * Example: <RoundedCard><CardContent>...</CardContent></RoundedCard>
 */
export const RoundedCard = styled(Card)(({ theme }) => ({
  borderRadius: shapes.card.large,
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
}));

/**
 * Styled FormControl with consistent modern styling
 * Example: <StyledFormControl><InputLabel>...</InputLabel><Select>...</Select></StyledFormControl>
 */
export const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    '&.Mui-focused': {
      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)',
    }
  }
}));

/**
 * Styled Select with centered text and modern styling
 * Example: <StyledSelect value={value} onChange={onChange} label="Label">...</StyledSelect>
 */
export const StyledSelect = styled(Select)(({ theme }) => ({
  '& .MuiSelect-select': {
    padding: '14px 40px 14px 14px',
    textAlign: 'center',
    minHeight: '20px',
    lineHeight: '20px'
  },
  '& .MuiSelect-icon': {
    right: '12px'
  }
}));
